import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';

import { MyRegistration } from '../../../core/interfaces/registration';
import { PaymentMethod } from '../../../core/interfaces/payment-method';
import { RegistrationStatusGuard } from '../../../core/guards/registration-status.guard';
import { PaymentMethodService } from '../../../core/services/payment-method.service';
import { RegistrationService } from '../../../core/services/registration.service';
import {
  UploadPurpose,
  UploadService,
} from '../../../core/services/uploads/upload.service';

import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe';
/**
 * Compte créé par une inscription en ligne, en attente de validation.
 *
 * <p>Le compte Keycloak est actif dès la soumission : l'élève définit son mot
 * de passe et se connecte. C'est ici qu'il dépose son ordre de virement, et
 * qu'il suit l'avancement — plutôt que de se heurter à un accès refusé sans
 * explication ni marche à suivre.</p>
 */
@Component({
  selector: 'app-registration-pending',
  standalone: true,
  imports: [CommonModule, TranslateModule, LocalizedDatePipe],
  templateUrl: './registration-pending.component.html',
  styleUrl: './registration-pending.component.css',
})
export class RegistrationPendingComponent implements OnInit {
  private readonly registrations = inject(RegistrationService);
  private readonly payments = inject(PaymentMethodService);
  private readonly uploads = inject(UploadService);
  private readonly guard = inject(RegistrationStatusGuard);
  private readonly keycloak = inject(KeycloakService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  readonly registration = signal<MyRegistration | null>(null);

  /**
   * « Effectuez le virement » sans coordonnées bancaires n'est pas une
   * consigne : les moyens actifs sont lus ici, à la même source que le site.
   */
  readonly paymentMethods = signal<PaymentMethod[]>([]);

  readonly loading = signal(true);
  readonly uploading = signal(false);
  readonly progress = signal<number | null>(null);
  readonly error = signal<string | null>(null);

  /** Tant que la demande n'est pas validée, le justificatif reste modifiable. */
  readonly canSubmitProof = computed(
    () => this.registration()?.status !== 'validated',
  );

  readonly hasProof = computed(() => !!this.registration()?.proofSubmittedAt);

  ngOnInit(): void {
    this.load();

    // Chargement indépendant : si aucun moyen n'est publié, la page reste
    // utilisable — le dépôt du justificatif ne dépend pas de cet affichage.
    this.payments.getActive().subscribe({
      next: (res) => this.paymentMethods.set(res.data ?? []),
      error: () => this.paymentMethods.set([]),
    });
  }

  /** Libellé du moyen de paiement dans la langue affichée. */
  label(method: PaymentMethod): string {
    return this.byLang(method.labelAr, method.labelFr, method.labelEn);
  }

  /** Marche à suivre dans la langue affichée ; vide si non saisie. */
  instructions(method: PaymentMethod): string | null {
    return this.byLang(
      method.instructionsAr,
      method.instructionsFr,
      method.instructionsEn,
    );
  }

  private byLang<T>(ar: T, fr: T, en: T): T {
    const lang = this.translate.currentLang || this.translate.defaultLang;
    return lang === 'ar' ? ar : lang === 'en' ? en : fr;
  }

  private load(): void {
    this.loading.set(true);
    this.registrations.getMine().subscribe({
      next: (res) => {
        this.registration.set(res.data);
        this.loading.set(false);
        // L'admin a validé entre-temps : on rend la main à l'application.
        if (res.data?.status === 'validated') this.enterApp();
      },
      error: () => {
        // Aucune demande (compte créé par l'administration) : rien à faire ici.
        this.loading.set(false);
        this.enterApp();
      },
    });
  }

  /** Statut → clé i18n, pour le libellé et la classe du bandeau. */
  get statusKey(): string {
    const status = this.registration()?.status ?? 'pending';
    return `REGISTRATION_PENDING.STATUS_${status.toUpperCase()}`;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.error.set(null);
    this.uploading.set(true);
    this.progress.set(0);

    // Dépôt en deux temps : le fichier part d'abord seul et rend une clé
    // opaque, que l'on rattache ensuite à la demande.
    this.uploads.uploadWithProgress(file, UploadPurpose.PaymentProof).subscribe({
      next: (event) => {
        this.progress.set(event.percent);
        if (!event.done || !event.result) return;

        this.registrations
          .submitProof({
            objectKey: event.result.key,
            originalFileName: event.result.originalFileName,
            contentType: event.result.contentType,
          })
          .subscribe({
            next: (res) => {
              this.registration.set(res.data);
              this.finishUpload(input);
            },
            error: (err) => this.failUpload(err, input),
          });
      },
      error: (err) => this.failUpload(err, input),
    });
  }

  private finishUpload(input: HTMLInputElement): void {
    this.uploading.set(false);
    this.progress.set(null);
    // Sans reset, re-sélectionner le même fichier ne déclencherait pas `change`.
    input.value = '';
  }

  private failUpload(err: unknown, input: HTMLInputElement): void {
    const message = (err as { error?: { message?: string } })?.error?.message;
    this.error.set(
      message || this.translate.instant('REGISTRATION_PENDING.UPLOAD_ERROR'),
    );
    this.finishUpload(input);
  }

  /** Re-vérifie le statut — l'élève relance après avoir été prévenu. */
  refresh(): void {
    this.guard.reset();
    this.load();
  }

  private enterApp(): void {
    this.guard.reset();
    this.router.navigate(['/student/dashboard']);
  }

  logout(): void {
    this.keycloak.logout(window.location.origin);
  }
}
